import { AsyncPipe, CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { addDays, isAfter } from 'date-fns';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';
import { MenuModule } from 'primeng/menu';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { BehaviorSubject, NEVER, switchMap, timer } from 'rxjs';

import { environment } from '../../../../../../environments/environment';
import { ColumnConfig } from '../../../../../shared/components/table-header/header-config.model';
import { TableHeaderComponent } from '../../../../../shared/components/table-header/table-header.component';
import { PaginationResponse } from '../../../../../shared/models/pagination-response.model';
import { D4TrainingLogService } from '../../../../home/d4-training-log.service';
import { TrainingLog } from '../../../../home/d4-training-log.service.model';
import { D4ActiveTaskListWidgetConfiguration } from '../../../models/d4-active-tasks-list-widget.model';
import {
  WidgetComponent,
  WidgetConfigurationType,
} from '../../../models/widget-configuration.model';
import { WidgetVisibilityService } from '../../../widget-visibility.service';
import { D4ActiveTasksListWidgetConfigurationComponent } from '../d4-active-tasks-list-widget-configuration/d4-active-tasks-list-widget-configuration.component';

@Component({
  selector: 'carehub-d4-active-tasks-list-widget',
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    TableModule,
    TableHeaderComponent,
    CommonModule,
    ButtonModule,
    CardModule,
    MenuModule,
    PaginatorModule,
    InputNumberModule,
    D4ActiveTasksListWidgetConfigurationComponent,
  ],
  templateUrl: './d4-active-tasks-list-widget.component.html',
  styleUrl: './d4-active-tasks-list-widget.component.scss',
  providers: [WidgetVisibilityService],
})
export class D4ActiveTasksListWidgetComponent
  implements AfterViewInit, OnInit, OnDestroy, WidgetComponent {
  @ViewChild('responsiveWidgetTable') tableCard!: ElementRef<HTMLDivElement>;
  @ViewChild('configurationComponent')
  configurationComponent!: D4ActiveTasksListWidgetConfigurationComponent;

  @Input() isEditing: boolean = false;
  @Input() configuration: D4ActiveTaskListWidgetConfiguration | undefined;
  _trainingLogResponse$: BehaviorSubject<PaginationResponse<TrainingLog[]> | null> =
    new BehaviorSubject<PaginationResponse<TrainingLog[]> | null>(null);

  @Input() configurationChange: EventEmitter<WidgetConfigurationType> =
    new EventEmitter<WidgetConfigurationType>();

  @Input() configurationDelete: EventEmitter<WidgetConfigurationType> =
    new EventEmitter<WidgetConfigurationType>();

  @Input() loadingChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  actionMenuItems: MenuItem[] = [
    {
      label: $localize`:@@common.button.edit:Edit`,
      command: () => this.onEdit(),
    },
    {
      label: $localize`:@@common.button.delete:Delete`,
      command: () => this.onDelete(),
    },
  ];

  isPaused$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  _headerConfig: ColumnConfig[] = [
    {
      header: $localize`:@@d4_list_widget.table.header.document:Document`,
      sortable: false,
      priority: 1,
      minWidth: 150,
    },
    {
      header: $localize`:@@d4_list_widget.table.header.send_date:Send date`,
      sortable: false,
      priority: 2,
      minWidth: 120,
    },
    {
      header: $localize`:@@d4_list_widget.table.header.due_date:Due date`,
      sortable: false,
      priority: 1,
      minWidth: 120,
    },
    { header: '', sortable: false },
  ];

  constructor(
    private _d4trainingLogService: D4TrainingLogService,
    private _widgetVisibilityService: WidgetVisibilityService,
    private _changeDetector: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this._widgetVisibilityService.setupResizeObserver(this.tableCard.nativeElement);
  }

  ngOnInit(): void {
    const reloadMilliseconds: number = (this.configuration?.reloadDelay ?? 5) * 1000 * 60;
    this.isPaused$.next(this.isEditing);
    this.isPaused$
      .pipe(switchMap(isPaused => (isPaused ? NEVER : timer(0, reloadMilliseconds))))
      .subscribe(() => {
        this.loadingChange.emit(true);
        this._d4trainingLogService.getActiveTrainingLogs().subscribe({
          next: (trainingLogs: PaginationResponse<TrainingLog[]>) => {
            this._trainingLogResponse$.next(trainingLogs);
            this.loadingChange.emit(false);
          },
          error: () => {
            this.loadingChange.emit(false);
          },
        });
      });
  }

  ngOnDestroy(): void {
    this.isPaused$.complete();
    this._widgetVisibilityService.disconnect();
  }

  get d4Config(): D4ActiveTaskListWidgetConfiguration | undefined {
    return this.configuration as D4ActiveTaskListWidgetConfiguration;
  }

  onEdit(): void {
    this.isEditing = true;
    this.isPaused$.next(true);
    this._changeDetector.detectChanges();
  }

  onConfigurationUpdate(): void {
    if (this.configurationComponent && this.configurationComponent.isValid()) {
      const newConfiguration: D4ActiveTaskListWidgetConfiguration =
        this.configurationComponent.getConfiguration() as D4ActiveTaskListWidgetConfiguration;

      this.isEditing = false;
      this.isPaused$.next(false);

      this.configurationChange.emit(newConfiguration);
    }
  }

  onConfigurationCancel(): void {
    this.isEditing = false;
    this.isPaused$.next(false);
  }

  isConfigurationValid(): boolean {
    return this.configurationComponent?.isValid() || false;
  }

  onDelete(): void {
    this.configurationDelete.emit(this.configuration);
  }

  getDocumentUrl(documentId: number): string {
    return environment.d4Url + documentId;
  }

  isDateExpired(date: Date): boolean {
    const now: Date = new Date();
    return isAfter(now, date);
  }

  isDateCloseToExpire(date: Date): boolean {
    const now: Date = new Date();
    const threeDaysInTheFuture: Date = addDays(now, 3);

    return isAfter(threeDaysInTheFuture, date);
  }

  onPageChange(paginator?: PaginatorState): void {
    const page: number = (paginator?.page ?? 0) + 1;
    const pageSize: number = paginator?.rows ?? 10;

    this.loadingChange.emit(true);
    this._d4trainingLogService.getActiveTrainingLogs(page, pageSize).subscribe({
      next: (trainingLogsResponse: PaginationResponse<TrainingLog[]>) => {
        this._trainingLogResponse$.next(trainingLogsResponse);
        this.loadingChange.emit(false);
      },
      error: () => {
        this.loadingChange.emit(false);
      },
    });
  }

  isColumnVisible(priority: number): boolean {
    return this._widgetVisibilityService.isColumnVisible(priority);
  }

  getVisibleHeaderConfig(): ColumnConfig[] {
    return this._widgetVisibilityService.getVisibleColumns(this._headerConfig);
  }
}
